import * as React from 'react';
import { Outlet, RouteObject } from 'react-router-dom';

import { OfferDashboardSkeleton, SkeletonLoader } from '@europrocurement/flexy-components';
import { ADMIN_OFFRES, KeycloakRoute, REALM_ADMIN } from '@europrocurement/l2d-keycloak';

import OfferConfigDashboard from './OfferConfigDashboard';
import AccountingConfigDashboard from './AccountingConfigDashboard';
import AdminGrilleTarifaire from './AdminGrilleTarifaire';
import { OfferFiche, OFFER_FICHE_ROUTE } from './Offer';
import { PackageFiche, PACKAGE_FICHE_ROUTE } from './Package';
import { CatalogFiche, CATALOG_FICHE_ROUTE } from './Catalog';
import { OfferAdditionalFieldFiche, ADDITIONAL_FIELD_FICHE_ROUTE } from './AdditionalField';
import { GroupFiche, GROUP_FICHE_ROUTE } from './Group';
import { ArticleFiche, ARTICLE_FICHE_ROUTE } from './Article';
import { ArticleCategoryFiche, ARTICLE_CATEGORY_FICHE_ROUTE } from './ArticleCategory';
import useOffresDashboardData from './hooks/useOffresDashboardData';

/**
 * Layout des écrans de configuration.
 *
 * Charge les données partagées (caches, collections du store) pour l'ensemble du
 * sous-arbre, afin que les fiches accédées en lien direct disposent des mêmes
 * données que le dashboard. L'index affiche le dashboard à onglets, les routes
 * enfantes affichent les fiches par objet.
 */
const ConfigLayout: React.FunctionComponent = function () {
    const { isLoading } = useOffresDashboardData();

    return (
        <SkeletonLoader
            isLoading={isLoading}
            skeleton={<OfferDashboardSkeleton />}
        >
            <Outlet />
        </SkeletonLoader>
    );
};

/**
 * Routes du module Offres :
 * - admin/offres : Configuration des offres commerciales (ADMIN_OFFRES)
 * - admin/configuration-comptabilite : Configuration comptabilité (REALM_ADMIN)
 * - admin/grille-tarifaire : Grille tarifaire (ADMIN_OFFRES)
 */
const OfferRouter: RouteObject[] = [
    {
        path: 'admin/offres',
        element: (
            <KeycloakRoute roles={ADMIN_OFFRES}>
                <ConfigLayout />
            </KeycloakRoute>
        ),
        handle: { title: 'Configuration des offres commerciales' },
        children: [
            { index: true, element: <OfferConfigDashboard /> },
            {
                path: OFFER_FICHE_ROUTE,
                element: <OfferFiche />,
                handle: { title: 'Fiche offre' },
            },
            {
                path: PACKAGE_FICHE_ROUTE,
                element: <PackageFiche />,
                handle: { title: 'Fiche package' },
            },
            {
                path: CATALOG_FICHE_ROUTE,
                element: <CatalogFiche />,
                handle: { title: 'Fiche catalogue' },
            },
            {
                path: ADDITIONAL_FIELD_FICHE_ROUTE,
                element: <OfferAdditionalFieldFiche />,
                handle: { title: 'Fiche champ complémentaire' },
            },
            {
                path: GROUP_FICHE_ROUTE,
                element: <GroupFiche />,
                handle: { title: 'Fiche groupement' },
            },
        ],
    },
    {
        path: 'admin/configuration-comptabilite',
        element: (
            <KeycloakRoute roles={REALM_ADMIN}>
                <ConfigLayout />
            </KeycloakRoute>
        ),
        handle: { title: 'Configuration comptabilité' },
        children: [
            { index: true, element: <AccountingConfigDashboard /> },
            {
                path: ARTICLE_FICHE_ROUTE,
                element: <ArticleFiche />,
                handle: { title: 'Fiche article' },
            },
            {
                path: ARTICLE_CATEGORY_FICHE_ROUTE,
                element: <ArticleCategoryFiche />,
                handle: { title: "Fiche catégorie d'article" },
            },
        ],
    },
    {
        path: 'admin/grille-tarifaire',
        element: (
            <KeycloakRoute roles={ADMIN_OFFRES}>
                <AdminGrilleTarifaire />
            </KeycloakRoute>
        ),
        handle: { title: 'Grille tarifaire' },
    },
];

export default OfferRouter;
